const TOPAZ_WRAPPER_ATTR = "SigPlusExtLiteWrapperURL";
const DEVICE_STATUS_READY = 1;
const MODEL_NUMBER_MAP = {
  11: "T-LBK462-HSB-R",
  12: "T-LBK462-HSB-R",
  15: "T-LBK460-HSB-R",
  57: "T-LBK57GC-BHSB-R",
};
const MODEL_FAMILY_1X5 = new Set([11, 12, 15]);
const MODEL_FAMILY_57 = new Set([57]);
const TOPAZ_57_SIGBOX_IMAGE_ASSET_PATH = "/topaz/sigBox.bmp";
const TOPAZ_DISCONNECT_TIMEOUT_MS = 1500;
// Guest never touched the pad: reject. Must be generous -- live traces show
// ~5s just to pick up the pen after the sign screen appears.
const TOPAZ_57_NO_INK_TIMEOUT_MS = 60000;
// Guest signed, stopped, and did not tap DONE: complete for them. Re-armed on
// every pen-up, so it only fires after they genuinely stop.
const TOPAZ_57_IDLE_FINISH_MS = 5000;
// Inactivity window after the guest's last pen-up before a 1x5 capture
// auto-completes. Only covers pauses WITHIN a signature (between names,
// dotting an i) -- it arms after the first stroke, so it is not about the
// guest reading the terms. The 1x5 pad has no DONE button, so this is a
// dead wait on every capture: long enough to clear a hesitant signer,
// short enough not to stall the front desk. 1.2s was too short and fired
// mid-signature; 20s made every check-in wait; 5s is the current balance.
const TOPAZ_1X5_AUTO_FINISH_MS = 5000;
// The sign-screen bitmap is now the full 640x480 LCD (white upper half with
// the DONE button and instructions, yellow signature band on the bottom).
// y/height below describe only the signature window within that full
// screen -- not the bitmap's own bounds -- and must stay in agreement with
// the matching crop from the SignResponse preview bitmap: moving the band
// without moving the crop silently breaks the on-screen mirroring.
// previewCropY keeps the empirical -18px offset carried over from the
// original 117px layout (band 363 -> crop 345).
const TOPAZ_57_SIG_BAND = {
  y: 240,
  height: 240,
  previewCropY: 222,
};

// The Topaz browser extension fetches this URL itself, not the page, so it
// must be absolute and reachable from the workstation. A bare path will not
// resolve for the extension.
const resolveSigBoxImageUrl = () => {
  try {
    return new URL(TOPAZ_57_SIGBOX_IMAGE_ASSET_PATH, window.location.origin)
      .href;
  } catch (error) {
    return TOPAZ_57_SIGBOX_IMAGE_ASSET_PATH;
  }
};

// window.top is defined for cross-origin frames, but touching .document
// throws SecurityError. Optional chaining does not help -- the throw
// precedes the || fallback.
const resolveTopDocument = () => {
  try {
    return window.top?.document || document;
  } catch (error) {
    return document;
  }
};

export const DEFAULT_TOPAZ_PROMPT_TEXT =
  "By signing, you have acknowledged and agreed with the terms and conditions set forth by the property.";
export const TOPAZ_CAPTURE_RESET_ERROR_CODE = "TOPAZ_CAPTURE_RESET";
const TOPAZ_DEBUG_LOG_KEY = "__topazDebugLog";

let wrapperLoadPromise = null;
let activeTopazCaptureSession = null;
let topazCaptureSequence = 0;

const summarizeError = (error) => {
  if (!error) {
    return null;
  }

  return {
    name: error?.name || null,
    message: error?.message || String(error),
    stack: error?.stack || null,
    responseStatus: error?.response?.status || null,
    responseData: error?.response?.data || null,
  };
};

export const logTopazDebug = (stage, details = {}) => {
  const entry = {
    ts: new Date().toISOString(),
    stage,
    ...details,
  };

  if (typeof window !== "undefined") {
    const existing = Array.isArray(window[TOPAZ_DEBUG_LOG_KEY])
      ? window[TOPAZ_DEBUG_LOG_KEY]
      : [];
    existing.push(entry);
    if (existing.length > 500) {
      existing.shift();
    }
    window[TOPAZ_DEBUG_LOG_KEY] = existing;
  }

  console.info("[TopazDebug]", entry);
  return entry;
};

const waitForWrapperUrl = (timeoutMs = 4000) =>
  new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      const wrapperUrl =
        document.documentElement.getAttribute(TOPAZ_WRAPPER_ATTR);
      if (wrapperUrl) {
        resolve(wrapperUrl);
        return;
      }

      if (Date.now() - start >= timeoutMs) {
        resolve(null);
        return;
      }

      window.setTimeout(poll, 150);
    };

    poll();
  });

const loadWrapperScript = async (wrapperUrl) => {
  if (window.Topaz) {
    return;
  }

  if (!wrapperLoadPromise) {
    wrapperLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = wrapperUrl;
      script.async = true;
      script.onload = resolve;
      script.onerror = () =>
        reject(new Error("Failed to load the SigPlusExtLite wrapper."));
      document.head.appendChild(script);
    });
  }

  await wrapperLoadPromise;
};

const normalizeSignatureBase64 = (signatureBase64) => {
  if (!signatureBase64) {
    return "";
  }

  if (signatureBase64.startsWith("data:")) {
    return signatureBase64.split(",")[1] || "";
  }

  return signatureBase64.trim();
};

const normalizeSupportedModels = (supportedModels = []) =>
  Array.isArray(supportedModels) ? supportedModels : [];

const getCompatibleModelNames = ({ rawModelNumber, modelNumber }) => {
  const names = [];
  const mappedModelName = MODEL_NUMBER_MAP[modelNumber];

  if (mappedModelName) {
    names.push(mappedModelName);
  }

  if (rawModelNumber != null) {
    names.push(`TOPAZ_MODEL_${rawModelNumber}`);
    names.push(String(rawModelNumber));
  }

  return Array.from(new Set(names.filter(Boolean)));
};

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const clearCanvas = (canvasElement) => {
  if (!canvasElement) {
    return;
  }

  const context = canvasElement.getContext("2d");
  if (context?.clearRect) {
    context.clearRect(0, 0, canvasElement.width, canvasElement.height);
  }
};

const drawSignaturePreview = (canvasElement, imageElement) => {
  if (!canvasElement || !imageElement) {
    return;
  }

  const context = canvasElement.getContext("2d");
  if (!context) {
    return;
  }

  const sourceWidth = imageElement.naturalWidth || imageElement.width;
  const sourceHeight = imageElement.naturalHeight || imageElement.height;
  if (!sourceWidth || !sourceHeight) {
    clearCanvas(canvasElement);
    return;
  }

  const probeCanvas = document.createElement("canvas");
  probeCanvas.width = sourceWidth;
  probeCanvas.height = sourceHeight;
  const probeContext = probeCanvas.getContext("2d");
  probeContext.drawImage(imageElement, 0, 0, sourceWidth, sourceHeight);

  let minX = sourceWidth;
  let minY = sourceHeight;
  let maxX = -1;
  let maxY = -1;

  try {
    const { data } = probeContext.getImageData(0, 0, sourceWidth, sourceHeight);
    for (let y = 0; y < sourceHeight; y += 1) {
      for (let x = 0; x < sourceWidth; x += 1) {
        const index = (y * sourceWidth + x) * 4;
        const alpha = data[index + 3];
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const isInk = alpha > 0 && (red < 245 || green < 245 || blue < 245);

        if (!isInk) {
          continue;
        }

        if (x < minX) {
          minX = x;
        }
        if (y < minY) {
          minY = y;
        }
        if (x > maxX) {
          maxX = x;
        }
        if (y > maxY) {
          maxY = y;
        }
      }
    }
  } catch (error) {
    minX = 0;
    minY = 0;
    maxX = sourceWidth - 1;
    maxY = sourceHeight - 1;
  }

  const hasInk = maxX >= minX && maxY >= minY;
  if (!hasInk) {
    return false;
  }

  context.clearRect(0, 0, canvasElement.width, canvasElement.height);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvasElement.width, canvasElement.height);

  const padding = 16;
  const cropX = hasInk ? Math.max(0, minX - padding) : 0;
  const cropY = hasInk ? Math.max(0, minY - padding) : 0;
  const cropWidth = hasInk
    ? Math.min(sourceWidth - cropX, maxX - minX + 1 + padding * 2)
    : sourceWidth;
  const cropHeight = hasInk
    ? Math.min(sourceHeight - cropY, maxY - minY + 1 + padding * 2)
    : sourceHeight;

  const scale = Math.min(
    canvasElement.width / cropWidth,
    canvasElement.height / cropHeight
  );
  const destWidth = cropWidth * scale;
  const destHeight = cropHeight * scale;
  const destX = (canvasElement.width - destWidth) / 2;
  const destY = (canvasElement.height - destHeight) / 2;

  context.drawImage(
    imageElement,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    destX,
    destY,
    destWidth,
    destHeight
  );

  return true;
};

const loadSignaturePreviewImage = async (signatureImage) => {
  const normalized = normalizeSignatureBase64(signatureImage);
  if (!normalized) {
    return null;
  }

  const candidates = signatureImage?.startsWith?.("data:")
    ? [signatureImage]
    : [
        `data:image/png;base64,${normalized}`,
        `data:image/jpeg;base64,${normalized}`,
      ];

  for (const candidate of candidates) {
    const loadedImage = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = candidate;
    });

    if (loadedImage) {
      return loadedImage;
    }
  }

  return null;
};

const refreshSignaturePreview = async ({ sign, canvasElement }) => {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const signatureImage = await sign.GetSignatureImage?.();
    if (signatureImage) {
      const loadedImage = await loadSignaturePreviewImage(signatureImage);
      if (loadedImage && drawSignaturePreview(canvasElement, loadedImage)) {
        return true;
      }
    }

    if (attempt < 3) {
      await sleep(120);
    }
  }

  return false;
};

const resizeCanvas = (canvasElement, width, height) => {
  if (!canvasElement) {
    return;
  }

  canvasElement.width = width;
  canvasElement.height = height;
  clearCanvas(canvasElement);
};

const ensureCanvasElement = (canvasElement) => {
  if (!canvasElement?.getContext) {
    throw new Error(
      "Topaz signature canvas is not available in the capture modal."
    );
  }
};

const getLastError = async (global) => {
  try {
    return (await global?.GetLastError?.()) || "";
  } catch (error) {
    return "";
  }
};

const getDisplayLines = (text, maxCharsPerLine) => {
  const normalizedText = (text || DEFAULT_TOPAZ_PROMPT_TEXT)
    .replace(/\s+/g, " ")
    .trim();

  if (!normalizedText) {
    return [DEFAULT_TOPAZ_PROMPT_TEXT];
  }

  const words = normalizedText.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length <= maxCharsPerLine) {
      currentLine = candidate;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    if (word.length <= maxCharsPerLine) {
      currentLine = word;
      return;
    }

    let remainder = word;
    while (remainder.length > maxCharsPerLine) {
      lines.push(remainder.slice(0, maxCharsPerLine));
      remainder = remainder.slice(maxCharsPerLine);
    }
    currentLine = remainder;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const paginateLines = (lines, linesPerPage) => {
  const pages = [];
  for (let index = 0; index < lines.length; index += linesPerPage) {
    pages.push(lines.slice(index, index + linesPerPage));
  }
  return pages.length > 0 ? pages : [[]];
};

const safeDisconnect = async (global) => {
  try {
    logTopazDebug("global.disconnect.start", {
      timeoutMs: TOPAZ_DISCONNECT_TIMEOUT_MS,
    });
    const disconnectOutcome = await Promise.race([
      Promise.resolve()
        .then(() => global?.Disconnect?.())
        .then(() => ({ status: "resolved" }))
        .catch((error) => ({ status: "error", error })),
      sleep(TOPAZ_DISCONNECT_TIMEOUT_MS).then(() => ({
        status: "timeout",
      })),
    ]);

    if (disconnectOutcome?.status === "timeout") {
      logTopazDebug("global.disconnect.timeout", {
        timeoutMs: TOPAZ_DISCONNECT_TIMEOUT_MS,
      });
      return;
    }

    if (disconnectOutcome?.status === "error") {
      throw disconnectOutcome.error;
    }

    logTopazDebug("global.disconnect.success");
  } catch (error) {
    logTopazDebug("global.disconnect.error", {
      error: summarizeError(error),
    });
    console.error("Failed to disconnect Topaz device cleanly:", error);
  }
};

const createTopazCaptureResetError = () => {
  const error = new Error("Topaz pad reset. Start capture again.");
  error.code = TOPAZ_CAPTURE_RESET_ERROR_CODE;
  return error;
};

export const isTopazCaptureResetError = (error) =>
  error?.code === TOPAZ_CAPTURE_RESET_ERROR_CODE;

const registerActiveCaptureSession = (session) => {
  activeTopazCaptureSession = session;
  logTopazDebug("capture.session.registered", {
    sessionLabel: session?.sessionLabel || null,
  });
};

const releaseActiveCaptureSession = (sessionId) => {
  if (activeTopazCaptureSession?.id === sessionId) {
    logTopazDebug("capture.session.released", {
      sessionLabel: activeTopazCaptureSession?.sessionLabel || null,
    });
    activeTopazCaptureSession = null;
  }
};

const cleanupPenListener = async (sign) => {
  try {
    await sign?.RemovePenUpEventListener?.();
  } catch (error) {
    // Older wrappers may not expose this method.
  }
};

const reset1x5PadDisplay = async ({ sign, lcdTablet }) => {
  try {
    await sign?.StopSign?.();
  } catch (error) {
    // Safe to ignore if the canvas session was not fully started.
  }

  try {
    await lcdTablet?.ClearSigWindow?.(1);
    await lcdTablet?.KeyPadClearHotSpotList?.();
    await lcdTablet?.SetLCDCaptureMode?.(1);
    await lcdTablet?.LCDRefresh?.(0, 0, 0, 240, 64);
    await lcdTablet?.ClearTablet?.();
  } catch (error) {
    // Ignore device reset errors.
  }

  try {
    await sign?.SetTabletState?.(0);
  } catch (error) {
    // Ignore state reset issues.
  }
};

const reset57PadDisplay = async ({
  sign,
  lcdTablet,
  signResponseHandler,
  topDocument,
}) => {
  if (signResponseHandler && topDocument?.removeEventListener) {
    topDocument.removeEventListener("SignResponse", signResponseHandler, false);
  }

  try {
    await sign?.StopSign?.();
  } catch (error) {
    // Safe to ignore if no explicit canvas sign session was opened.
  }

  try {
    await lcdTablet?.ClearSigWindow?.(1);
    await lcdTablet?.KeyPadClearHotSpotList?.();
    await lcdTablet?.LCDRefresh?.(0, 0, 0, 640, 480);
    await lcdTablet?.SetLCDCaptureMode?.(1);
    await lcdTablet?.ClearTablet?.();
  } catch (error) {
    // Ignore device reset errors.
  }

  try {
    await sign?.SetTabletState?.(0);
  } catch (error) {
    // Ignore state reset issues.
  }
};

const cleanup1x5Session = async ({ sign, lcdTablet, previousOnSignPenUp }) => {
  window.OnSignPenUp = previousOnSignPenUp;
  await cleanupPenListener(sign);
  await reset1x5PadDisplay({ sign, lcdTablet });
};

const cleanup57Session = async ({
  sign,
  lcdTablet,
  signResponseHandler,
  previousOnSignPenUp,
  topDocument,
}) => {
  window.OnSignPenUp = previousOnSignPenUp;
  await cleanupPenListener(sign);
  await reset57PadDisplay({
    sign,
    lcdTablet,
    signResponseHandler,
    topDocument,
  });
};

const write1x5PromptScreen = async ({
  lcdTablet,
  lines,
  isFinalPromptPage,
  canGoBack,
}) => {
  await lcdTablet.KeyPadClearHotSpotList();
  await lcdTablet.ClearSigWindow(1);
  await lcdTablet.SetLCDCaptureMode(2);
  await lcdTablet.LCDRefresh(0, 0, 0, 240, 64);
  await lcdTablet.SetJustifyMode(0);

  for (const [index, line] of lines.entries()) {
    const yPos = 2 + index * 18;
    await lcdTablet.LCDWriteString(0, 2, 15, yPos, line);
  }

  if (canGoBack) {
    await lcdTablet.LCDWriteString(0, 2, 200, 42, "Back");
    await lcdTablet.KeyPadAddHotSpot(1, 1, 182, 34, 52, 24);
  }

  await lcdTablet.LCDWriteString(
    0,
    2,
    35,
    44,
    isFinalPromptPage ? "SIGN" : "CONTINUE"
  );
  await lcdTablet.KeyPadAddHotSpot(0, 1, 22, 34, 88, 24);
  await lcdTablet.ClearTablet();
  await lcdTablet.LCDSetWindow(0, 0, 1, 1);
  await lcdTablet.SetSigWindow(1, 0, 0, 1, 1);
  await lcdTablet.SetLCDCaptureMode(2);
};

const write1x5SignScreen = async (lcdTablet, canvasElement, message = "") => {
  await lcdTablet.KeyPadClearHotSpotList();
  await lcdTablet.LCDRefresh(0, 0, 0, 240, 64);
  await lcdTablet.ClearSigWindow(1);

  if (message) {
    await lcdTablet.LCDWriteString(0, 2, 8, 22, message.slice(0, 28));
    await lcdTablet.LCDSetWindow(0, 0, 1, 1);
    await lcdTablet.SetSigWindow(1, 0, 0, 1, 1);
    await lcdTablet.ClearTablet();
    return;
  }

  await lcdTablet.LCDSetWindow(0, 0, 240, 64);
  await lcdTablet.SetSigWindow(1, 0, 0, 240, 64);
  await lcdTablet.ClearTablet();
  clearCanvas(canvasElement);
};

const captureFrom1x5Pad = async ({
  sign,
  lcdTablet,
  canvasElement,
  promptText,
}) =>
  new Promise(async (resolve, reject) => {
    const sessionLabel = `1x5-${Date.now()}-${++topazCaptureSequence}`;
    let finished = false;
    let syncPenUp = false;
    let promptPageIndex = 0;
    let isSigning = false;
    let autoFinishTimerId = null;
    let watchdogTimerId = null;
    let lastPenUpAt = null;
    const sessionId = Symbol("topaz-1x5");
    const sessionState = {
      cancelled: false,
    };
    const previousOnSignPenUp = window.OnSignPenUp;
    const promptPages = paginateLines(getDisplayLines(promptText, 34), 2);
    logTopazDebug("1x5.capture.start", {
      sessionLabel,
      promptPagesCount: promptPages.length,
      promptPageLineCounts: promptPages.map((page) => page.length),
      promptTextLength: promptText?.length || 0,
    });

    const clearAutoFinishTimer = () => {
      if (autoFinishTimerId) {
        window.clearTimeout(autoFinishTimerId);
        autoFinishTimerId = null;
      }
    };

    const clearWatchdogTimer = () => {
      if (watchdogTimerId) {
        window.clearInterval(watchdogTimerId);
        watchdogTimerId = null;
      }
    };

    const finishCapture = async () => {
      if (finished || sessionState.cancelled) {
        return;
      }

      finished = true;
      clearAutoFinishTimer();
      clearWatchdogTimer();
      try {
        logTopazDebug("1x5.capture.finish.start", {
          sessionLabel,
        });
        const totalPoints = await sign.GetTotalPoints();
        logTopazDebug("1x5.capture.finish.points", {
          sessionLabel,
          totalPoints,
        });
        if (!totalPoints || totalPoints <= 0) {
          throw new Error("Guest signature was not captured.");
        }

        await sign.SetSigStringFormat(0, promptText, 1);
        const signatureString = (await sign.GetSigString?.()) || "";
        const signatureImage = await sign.GetSignatureImage();
        logTopazDebug("1x5.capture.finish.payload-ready", {
          sessionLabel,
          signatureStringLength: signatureString.length,
          signatureImageLength: signatureImage?.length || 0,
        });
        resolve({
          signatureBase64: normalizeSignatureBase64(signatureImage),
          signatureString,
        });
      } catch (error) {
        logTopazDebug("1x5.capture.finish.error", {
          sessionLabel,
          error: summarizeError(error),
        });
        if (!sessionState.cancelled) {
          reject(error);
        }
      } finally {
        logTopazDebug("1x5.capture.cleanup.start", {
          sessionLabel,
        });
        await cleanup1x5Session({
          sign,
          lcdTablet,
          previousOnSignPenUp,
        });
        releaseActiveCaptureSession(sessionId);
        logTopazDebug("1x5.capture.cleanup.done", {
          sessionLabel,
        });
      }
    };

    const showPromptPage = async () => {
      isSigning = false;
      clearAutoFinishTimer();
      logTopazDebug("1x5.prompt.show", {
        sessionLabel,
        promptPageIndex,
        isFinalPromptPage: promptPageIndex === promptPages.length - 1,
        lines: promptPages[promptPageIndex],
      });
      return write1x5PromptScreen({
        lcdTablet,
        lines: promptPages[promptPageIndex],
        isFinalPromptPage: promptPageIndex === promptPages.length - 1,
        canGoBack: promptPageIndex > 0,
      });
    };

    const showSignScreen = async (message = "") => {
      isSigning = !message;
      clearAutoFinishTimer();
      logTopazDebug("1x5.sign-screen.show", {
        sessionLabel,
        isSigning,
        message,
      });
      return write1x5SignScreen(lcdTablet, canvasElement, message);
    };

    const scheduleAutoFinish = () => {
      if (!isSigning || finished || sessionState.cancelled) {
        logTopazDebug("1x5.auto-finish.skipped", {
          sessionLabel,
          isSigning,
          finished,
          cancelled: sessionState.cancelled,
        });
        return;
      }

      clearAutoFinishTimer();
      logTopazDebug("1x5.auto-finish.scheduled", {
        sessionLabel,
        timeoutMs: TOPAZ_1X5_AUTO_FINISH_MS,
      });
      autoFinishTimerId = window.setTimeout(async () => {
        autoFinishTimerId = null;

        if (finished || sessionState.cancelled || !isSigning) {
          logTopazDebug("1x5.auto-finish.cancelled-before-run", {
            sessionLabel,
            finished,
            cancelled: sessionState.cancelled,
            isSigning,
          });
          return;
        }

        try {
          const totalPoints = await sign.GetTotalPoints();
          logTopazDebug("1x5.auto-finish.points", {
            sessionLabel,
            totalPoints,
          });
          if (totalPoints > 0) {
            await finishCapture();
          }
        } catch (error) {
          finished = true;
          clearWatchdogTimer();
          logTopazDebug("1x5.auto-finish.error", {
            sessionLabel,
            error: summarizeError(error),
          });
          if (!sessionState.cancelled) {
            reject(error);
          }
          await cleanup1x5Session({
            sign,
            lcdTablet,
            previousOnSignPenUp,
          });
          releaseActiveCaptureSession(sessionId);
        }
      }, TOPAZ_1X5_AUTO_FINISH_MS);
    };

    registerActiveCaptureSession({
      id: sessionId,
      sessionLabel,
      canvasElement,
      reject: (error) => {
        if (!finished) {
          finished = true;
          clearWatchdogTimer();
          logTopazDebug("1x5.capture.rejected", {
            sessionLabel,
            error: summarizeError(error),
          });
          reject(error);
        }
      },
      reset: async () => {
        sessionState.cancelled = true;
        clearAutoFinishTimer();
        clearWatchdogTimer();
        logTopazDebug("1x5.capture.reset", {
          sessionLabel,
        });
        await cleanup1x5Session({
          sign,
          lcdTablet,
          previousOnSignPenUp,
        });
      },
    });

    window.OnSignPenUp = async () => {
      if (finished || syncPenUp || sessionState.cancelled) {
        logTopazDebug("1x5.penup.ignored", {
          sessionLabel,
          finished,
          syncPenUp,
          cancelled: sessionState.cancelled,
        });
        return;
      }

      syncPenUp = true;
      try {
        logTopazDebug("1x5.penup.start", {
          sessionLabel,
          isSigning,
          promptPageIndex,
        });
        await sign.SetTabletState(1);

        if (isSigning) {
          const totalPoints = await sign.GetTotalPoints();
          logTopazDebug("1x5.penup.signing.points", {
            sessionLabel,
            totalPoints,
          });
          if (totalPoints > 0) {
            const penUpAt = Date.now();
            const msSinceLastPenUp =
              lastPenUpAt === null ? null : penUpAt - lastPenUpAt;
            lastPenUpAt = penUpAt;
            logTopazDebug("1x5.penup.gap", {
              msSinceLastPenUp,
              totalPoints,
              autoFinishMs: TOPAZ_1X5_AUTO_FINISH_MS,
            });
            scheduleAutoFinish();
          }
        } else {
          const continueTapped = (await lcdTablet.KeyPadQueryHotSpot(0)) > 0;
          const backTapped = (await lcdTablet.KeyPadQueryHotSpot(1)) > 0;
          logTopazDebug("1x5.penup.prompt.hotspots", {
            sessionLabel,
            continueTapped,
            backTapped,
            promptPageIndex,
          });
          if (continueTapped) {
            if (promptPageIndex < promptPages.length - 1) {
              promptPageIndex += 1;
              await showPromptPage();
            } else {
              await showSignScreen();
            }
          } else if (backTapped) {
            if (promptPageIndex > 0) {
              promptPageIndex -= 1;
              await showPromptPage();
            }
          } else {
            logTopazDebug("1x5.penup.no-hotspot-match", {
              sessionLabel,
              promptPageIndex,
            });
          }
        }
      } catch (error) {
        finished = true;
        clearAutoFinishTimer();
        clearWatchdogTimer();
        logTopazDebug("1x5.penup.error", {
          sessionLabel,
          error: summarizeError(error),
        });
        if (!sessionState.cancelled) {
          reject(error);
        }
        await cleanup1x5Session({
          sign,
          lcdTablet,
          previousOnSignPenUp,
        });
        releaseActiveCaptureSession(sessionId);
      } finally {
        syncPenUp = false;
        logTopazDebug("1x5.penup.done", {
          sessionLabel,
        });
      }
    };

    try {
      resizeCanvas(canvasElement, 600, 160);
      logTopazDebug("1x5.capture.init", {
        sessionLabel,
      });
      await sign.AddPenUpEventListener();
      await sign.SetTabletState(1);
      await sign.StartSign(canvasElement);
      await lcdTablet.SetLCDCaptureMode(2);
      watchdogTimerId = window.setInterval(() => {
        logTopazDebug("1x5.capture.watchdog", {
          sessionLabel,
          finished,
          isSigning,
          promptPageIndex,
          cancelled: sessionState.cancelled,
        });
      }, 15000);
      await showPromptPage();
    } catch (error) {
      finished = true;
      clearAutoFinishTimer();
      clearWatchdogTimer();
      logTopazDebug("1x5.capture.init.error", {
        sessionLabel,
        error: summarizeError(error),
      });
      if (!sessionState.cancelled) {
        reject(error);
      }
      await cleanup1x5Session({
        sign,
        lcdTablet,
        previousOnSignPenUp,
      });
      releaseActiveCaptureSession(sessionId);
    }
  });

const write57PromptScreen = async ({
  lcdTablet,
  lines,
  isFinalPromptPage,
  canGoBack,
}) => {
  await lcdTablet.KeyPadClearHotSpotList();
  await lcdTablet.ClearSigWindow(1);
  await lcdTablet.LCDRefresh(0, 0, 0, 640, 480);
  await lcdTablet.SetLCDCaptureMode(2);

  try {
    await lcdTablet.LCDSetFont(18, 16, 0, 0, 0, "Verdana");
  } catch (error) {
    // Font selection is optional on some firmware combinations.
  }

  for (const [index, line] of lines.entries()) {
    const yPos = 28 + index * 36;
    await lcdTablet.LCDWriteString(0, 2, 20, yPos, line);
  }

  if (canGoBack) {
    await lcdTablet.LCDWriteString(0, 2, 360, 420, "BACK");
    await lcdTablet.KeyPadAddHotSpot(1, 1, 350, 405, 90, 40);
  }

  await lcdTablet.LCDWriteString(
    0,
    2,
    20,
    420,
    isFinalPromptPage ? "CONTINUE TO SIGN" : "CONTINUE"
  );
  await lcdTablet.KeyPadAddHotSpot(0, 1, 20, 405, 240, 40);
  await lcdTablet.LCDSetWindow(0, 0, 1, 1);
  await lcdTablet.SetSigWindow(1, 0, 0, 1, 1);
  await lcdTablet.ClearTablet();
};

const write57SignScreen = async (lcdTablet, canvasElement, message = "") => {
  await lcdTablet.KeyPadClearHotSpotList();
  await lcdTablet.ClearSigWindow(1);
  await lcdTablet.LCDRefresh(0, 0, 0, 640, 480);

  try {
    await lcdTablet.LCDSetFont(20, 18, 0, 0, 0, "Verdana");
  } catch (error) {
    // Font selection is optional on some firmware combinations.
  }

  if (message) {
    await lcdTablet.LCDWriteString(0, 2, 20, 250, message);
    await lcdTablet.LCDSetWindow(0, 0, 1, 1);
    await lcdTablet.SetSigWindow(1, 0, 0, 1, 1);
    await lcdTablet.ClearTablet();
    return;
  }

  try {
    await lcdTablet.SetLCDPixelDepth?.(8);
  } catch (error) {
    // Pixel depth control is optional across firmware revisions.
  }

  try {
    await lcdTablet.LCDWriteImage?.(
      1,
      2,
      0,
      0,
      640,
      480,
      0,
      resolveSigBoxImageUrl()
    );
    await lcdTablet.LCDRefresh?.(2, 0, 0, 640, 480);
  } catch (error) {
    // The guest can still sign without the box graphic; do not abort.
    logTopazDebug("57.sign-screen.image-failed", {
      error: summarizeError(error),
    });
  }

  // Hotspot must be registered BEFORE the signature window is defined --
  // registering it afterwards leaves GetTotalPoints() at 0 even though the
  // pad echoes ink to the LCD. write57PromptScreen uses this same order.
  await lcdTablet.KeyPadAddHotSpot(0, 1, 465, 175, 140, 50);
  await lcdTablet.LCDSetWindow(0, TOPAZ_57_SIG_BAND.y, 640, TOPAZ_57_SIG_BAND.height);
  await lcdTablet.SetSigWindow(1, 0, TOPAZ_57_SIG_BAND.y, 640, TOPAZ_57_SIG_BAND.height + 1);
  try {
    await lcdTablet.SetLCDPixelDepth?.(0);
  } catch (error) {
    // Safe to ignore when not supported.
  }
  await lcdTablet.SetLCDCaptureMode(2);
  clearCanvas(canvasElement);
};

const captureFrom57Pad = async ({
  sign,
  lcdTablet,
  canvasElement,
  promptText,
}) =>
  new Promise(async (resolve, reject) => {
    let finished = false;
    let syncPenUp = false;
    let isSigning = true;
    const sessionId = Symbol("topaz-57");
    const sessionState = {
      cancelled: false,
    };
    const previousOnSignPenUp = window.OnSignPenUp;
    const topDocument = resolveTopDocument();

    const signResponseHandler = (event) => {
      const responseObj = event?.target?.getAttribute?.("msg-Attribute");
      if (!responseObj) {
        return;
      }

      try {
        const payload = JSON.parse(responseObj);
        if (!payload?.imageData) {
          return;
        }

        const img = new Image();
        img.onload = () => {
          const context = canvasElement.getContext("2d");
          if (!context) {
            return;
          }
          context.clearRect(0, 0, canvasElement.width, canvasElement.height);
          context.drawImage(
            img,
            0,
            TOPAZ_57_SIG_BAND.previewCropY,
            canvasElement.width,
            canvasElement.height,
            0,
            0,
            canvasElement.width,
            canvasElement.height
          );
        };
        img.src = `data:image/jpeg;base64,${payload.imageData}`;
      } catch (error) {
        console.error("Failed to render Topaz SignResponse image:", error);
      }
    };

    let activityTimerId = null;

    const clearActivityWatchdog = () => {
      if (activityTimerId) {
        window.clearTimeout(activityTimerId);
        activityTimerId = null;
      }
    };

    const armActivityWatchdog = (delayMs) => {
      clearActivityWatchdog();
      activityTimerId = window.setTimeout(async () => {
        activityTimerId = null;
        if (finished || sessionState.cancelled) {
          return;
        }
        let totalPoints = 0;
        try {
          totalPoints = await sign.GetTotalPoints();
        } catch (error) {
          totalPoints = 0;
        }
        // finishCapture (or a reset) may have settled the promise while we
        // awaited the SDK. Re-check before acting on a stale reading.
        if (finished || sessionState.cancelled) {
          return;
        }
        if (totalPoints > 0) {
          logTopazDebug("57.capture.activity-timeout.autofinish", {
            totalPoints,
            timeoutMs: delayMs,
          });
          try {
            await finishCapture();
          } catch (finishError) {
            logTopazDebug("57.capture.activity-timeout.finish-failed", {
              error: summarizeError(finishError),
            });
          }
          return;
        }
        finished = true;
        logTopazDebug("57.capture.activity-timeout", {
          timeoutMs: delayMs,
        });
        reject(
          new Error(
            "No signature activity detected from the Topaz pad. Reset the pad and try again."
          )
        );
        try {
          await cleanup57Session({
            sign,
            lcdTablet,
            signResponseHandler,
            previousOnSignPenUp,
            topDocument,
          });
        } catch (cleanupError) {
          logTopazDebug("57.capture.activity-timeout.cleanup-failed", {
            error: summarizeError(cleanupError),
          });
        } finally {
          releaseActiveCaptureSession(sessionId);
        }
      }, delayMs);
    };

    const finishCapture = async () => {
      if (finished || sessionState.cancelled) {
        return;
      }

      finished = true;
      clearActivityWatchdog();
      try {
        const totalPoints = await sign.GetTotalPoints();
        if (!totalPoints || totalPoints <= 0) {
          throw new Error("Guest signature was not captured.");
        }

        const signatureString = (await sign.GetSigString?.()) || "";
        const signatureImage = await sign.GetSignatureImage();
        resolve({
          signatureBase64: normalizeSignatureBase64(signatureImage),
          signatureString,
        });
      } catch (error) {
        if (!sessionState.cancelled) {
          reject(error);
        }
      } finally {
        await cleanup57Session({
          sign,
          lcdTablet,
          signResponseHandler,
          previousOnSignPenUp,
          topDocument,
        });
        releaseActiveCaptureSession(sessionId);
      }
    };

    const showSignScreen = async (message = "") => {
      isSigning = !message;
      clearActivityWatchdog();
      const result = await write57SignScreen(lcdTablet, canvasElement, message);
      if (isSigning) {
        armActivityWatchdog(TOPAZ_57_NO_INK_TIMEOUT_MS);
      }
      return result;
    };

    registerActiveCaptureSession({
      id: sessionId,
      canvasElement,
      reject: (error) => {
        if (!finished) {
          finished = true;
          reject(error);
        }
      },
      reset: async () => {
        sessionState.cancelled = true;
        clearActivityWatchdog();
        await cleanup57Session({
          sign,
          lcdTablet,
          signResponseHandler,
          previousOnSignPenUp,
          topDocument,
        });
      },
    });

    window.OnSignPenUp = async () => {
      if (finished || syncPenUp || sessionState.cancelled) {
        return;
      }

      syncPenUp = true;
      try {
        await sign.SetTabletState(1);
        if (isSigning) {
          const doneTapped = (await lcdTablet.KeyPadQueryHotSpot(0)) > 0;
          logTopazDebug("57.penup.done-hotspot", { doneTapped });
          if (doneTapped) {
            await finishCapture();
          } else {
            const totalPoints = await sign.GetTotalPoints();
            logTopazDebug("57.penup.points", { totalPoints });
            if (totalPoints > 0) {
              armActivityWatchdog(TOPAZ_57_IDLE_FINISH_MS);
            }
          }
        }
      } catch (error) {
        finished = true;
        clearActivityWatchdog();
        if (!sessionState.cancelled) {
          reject(error);
        }
        await cleanup57Session({
          sign,
          lcdTablet,
          signResponseHandler,
          previousOnSignPenUp,
          topDocument,
        });
        releaseActiveCaptureSession(sessionId);
      } finally {
        syncPenUp = false;
      }
    };

    try {
      resizeCanvas(canvasElement, 640, TOPAZ_57_SIG_BAND.height);
      topDocument.addEventListener("SignResponse", signResponseHandler, false);
      await sign.SetSigStringFormat(0, promptText, 1);
      await sign.AddPenUpEventListener();
      await sign.SetTabletState(1);
      await lcdTablet.SetImageWidth?.(640);
      await lcdTablet.SetImageHeight?.(480);
      await lcdTablet.KeyPadClearHotSpotList();
      await lcdTablet.ClearSigWindow(1);
      await lcdTablet.ClearTablet();
      await lcdTablet.SetLCDCaptureMode(2);
      await showSignScreen();
    } catch (error) {
      finished = true;
      clearActivityWatchdog();
      if (!sessionState.cancelled) {
        reject(error);
      }
      await cleanup57Session({
        sign,
        lcdTablet,
        signResponseHandler,
        previousOnSignPenUp,
        topDocument,
      });
      releaseActiveCaptureSession(sessionId);
    }
  });

const resolveDeviceError = async (global, deviceStatus) => {
  if (deviceStatus === 0) {
    return "No Topaz signature pad is ready on this workstation.";
  }
  if (deviceStatus === -2) {
    return "Topaz SigPlusExtLite is installed but this workstation does not support the requested feature.";
  }
  if (deviceStatus === -3) {
    return "Topaz SigPlusExtLite could not find the required SigPlus drivers.";
  }

  const lastError = await getLastError(global);
  if (lastError) {
    return lastError;
  }

  return "Topaz signature pad is not ready on this workstation.";
};

const resolveTabletIdentity = async ({ sign, lcdTablet }) => {
  let rawModelNumber = null;
  let serialNumber = "";

  try {
    await sign?.SetTabletState?.(1);
    rawModelNumber = await lcdTablet?.GetTabletModelNumber?.();
    serialNumber = (await lcdTablet?.GetTabletSerialNumber?.()) || "";
  } finally {
    try {
      await sign?.SetTabletState?.(0);
    } catch (error) {
      // Ignore state reset issues during model detection.
    }
  }

  return {
    rawModelNumber,
    modelNumber: Number(rawModelNumber),
    serialNumber,
  };
};

export const resetTopazCaptureSession = async ({ canvasElement } = {}) => {
  logTopazDebug("capture.reset.requested", {
    hasActiveSession: Boolean(activeTopazCaptureSession),
  });
  const activeSession = activeTopazCaptureSession;

  if (activeSession) {
    activeTopazCaptureSession = null;
    await activeSession.reset?.();
    clearCanvas(activeSession.canvasElement || canvasElement);
    if (activeSession.reject) {
      activeSession.reject(createTopazCaptureResetError());
    }
    logTopazDebug("capture.reset.completed", {
      sessionLabel: activeSession?.sessionLabel || null,
    });
    return true;
  }

  clearCanvas(canvasElement);

  const wrapperUrl = await waitForWrapperUrl(1000);
  if (!wrapperUrl) {
    return false;
  }

  await loadWrapperScript(wrapperUrl);

  if (!window.Topaz?.Global || !window.Topaz?.Canvas?.Sign) {
    return false;
  }

  const global = window.Topaz.Global;
  const sign = window.Topaz.Canvas.Sign;
  const lcdTablet = window.Topaz.Canvas.LCDTablet;

  try {
    logTopazDebug("capture.reset.connect.start");
    await global.Connect?.();

    const deviceStatus = await global.GetDeviceStatus?.();
    logTopazDebug("capture.reset.device-status", { deviceStatus });
    if (deviceStatus !== DEVICE_STATUS_READY) {
      return false;
    }

    const { modelNumber } = await resolveTabletIdentity({
      sign,
      lcdTablet,
    });

    if (MODEL_FAMILY_57.has(modelNumber)) {
      await reset57PadDisplay({
        sign,
        lcdTablet,
        topDocument: resolveTopDocument(),
      });
    } else {
      await reset1x5PadDisplay({
        sign,
        lcdTablet,
      });
    }

    return true;
  } finally {
    await safeDisconnect(global);
  }
};

export const captureSignatureFromTopaz = async ({
  canvasElement,
  supportedModels = [],
  promptText = DEFAULT_TOPAZ_PROMPT_TEXT,
} = {}) => {
  ensureCanvasElement(canvasElement);
  logTopazDebug("capture.start", {
    supportedModels,
    promptTextLength: promptText?.length || 0,
  });

  const wrapperUrl = await waitForWrapperUrl();
  if (!wrapperUrl) {
    logTopazDebug("capture.wrapper.missing");
    throw new Error(
      "SigPlusExtLite is not available in this browser. Install the Topaz browser extension on this workstation."
    );
  }
  logTopazDebug("capture.wrapper.detected", {
    wrapperUrl,
  });

  await loadWrapperScript(wrapperUrl);
  logTopazDebug("capture.wrapper.loaded");

  if (!window.Topaz?.Global || !window.Topaz?.Canvas?.Sign) {
    logTopazDebug("capture.wrapper.invalid");
    throw new Error("SigPlusExtLite did not initialize correctly.");
  }

  const allowedModels = normalizeSupportedModels(supportedModels);
  const global = window.Topaz.Global;
  const sign = window.Topaz.Canvas.Sign;
  const lcdTablet = window.Topaz.Canvas.LCDTablet;

  try {
    logTopazDebug("capture.connect.start");
    await global.Connect?.();
    logTopazDebug("capture.connect.success");

    const deviceStatus = await global.GetDeviceStatus();
    logTopazDebug("capture.device-status", {
      deviceStatus,
    });
    if (deviceStatus !== DEVICE_STATUS_READY) {
      throw new Error(await resolveDeviceError(global, deviceStatus));
    }

    const { rawModelNumber, modelNumber, serialNumber } =
      await resolveTabletIdentity({
        sign,
        lcdTablet,
      });
    const modelName =
      MODEL_NUMBER_MAP[modelNumber] ||
      `TOPAZ_MODEL_${rawModelNumber || "UNKNOWN"}`;
    const compatibleModelNames = getCompatibleModelNames({
      rawModelNumber,
      modelNumber,
    });
    logTopazDebug("capture.device-identity", {
      rawModelNumber,
      modelNumber,
      modelName,
      compatibleModelNames,
      serialNumber,
      allowedModels,
    });

    if (
      allowedModels.length > 0 &&
      !compatibleModelNames.some((supportedModel) =>
        allowedModels.includes(supportedModel)
      )
    ) {
      throw new Error(
        `The connected Topaz pad (${modelName}) is not enabled for this property.`
      );
    }

    let captureResult;

    if (MODEL_FAMILY_1X5.has(modelNumber)) {
      logTopazDebug("capture.family.selected", {
        family: "1x5",
        modelName,
      });
      captureResult = await captureFrom1x5Pad({
        sign,
        lcdTablet,
        canvasElement,
        promptText,
      });
    } else if (MODEL_FAMILY_57.has(modelNumber)) {
      logTopazDebug("capture.family.selected", { family: "57", modelName });
      captureResult = await captureFrom57Pad({
        sign,
        lcdTablet,
        canvasElement,
        promptText,
      });
    } else {
      logTopazDebug("capture.family.unsupported", {
        modelName,
        modelNumber,
      });
      throw new Error(
        `The connected Topaz pad (${modelName}) is not supported in the current PMS Topaz v1 flow. Use a supported 1x5 pad such as T-LBK462-HSB-R or T-LBK460-HSB-R.`
      );
    }

    logTopazDebug("capture.success", {
      modelName,
      modelNumber,
      serialNumber,
      signatureStringLength: captureResult?.signatureString?.length || 0,
      signatureBase64Length: captureResult?.signatureBase64?.length || 0,
    });
    return {
      modelName,
      modelNumber,
      serialNumber,
      promptText,
      ...captureResult,
    };
  } catch (error) {
    logTopazDebug("capture.error", {
      error: summarizeError(error),
    });
    throw error;
  } finally {
    await safeDisconnect(global);
  }
};
